import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useTranslations } from 'use-intl';
import { Mail, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ServiceRegistry } from '@/services/ServiceRegistry';
import { ConnectGmailDialog } from '@/components/messages/ConnectGmailDialog';
import { useRelativeTime } from '@/i18n/useRelativeTime';
import type { EmailProviderConnection } from '@/types/emailThread';

interface ConnectedMailboxesProps {
  /** null = platform-admin view (platform-wide connections); a company id = that company's
   * connections. Passed straight through to ConnectGmailDialog too, so "connect another"
   * creates a connection in the same scope this list is showing. */
  companyId: string | null;
  companyName?: string | null;
}

const syncStatusTone: Record<string, 'default' | 'destructive' | 'secondary'> = {
  idle: 'secondary',
  syncing: 'secondary',
  error: 'destructive',
};

// The statuses with a catalog label (`consultantMessages.mailboxes.syncStatus`); any other shows as sent.
function isSyncStatus(status: string): status is 'idle' | 'syncing' | 'error' {
  return status in syncStatusTone;
}

/**
 * Shows a compact summary of connected mailboxes in the Messages header, with the full list
 * (and disconnect actions) behind a modal — found missing during manual testing (issue #290
 * follow-up): connecting was previously a one-way action, "Connect Gmail" kept appearing as the
 * only affordance even after a successful connect, with no way to see what was already
 * connected. Listing every connection inline in the header (the first version of this
 * component) turned out to overflow the page once there were more than one or two connections,
 * especially with long addresses — a "N connected" summary + modal keeps the header a fixed
 * size regardless of how many mailboxes are connected.
 */
export function ConnectedMailboxes({ companyId, companyName }: ConnectedMailboxesProps) {
  const t = useTranslations('consultantMessages.mailboxes');
  const relativeTime = useRelativeTime();
  const [connections, setConnections] = useState<EmailProviderConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [connectOpen, setConnectOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    ServiceRegistry.getEmailThreadService()
      .listConnections(companyId ?? undefined)
      .then(setConnections)
      .catch(() => setConnections([]))
      .finally(() => setLoading(false));
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDisconnect = async (connectionId: string) => {
    setDisconnectingId(connectionId);
    try {
      await ServiceRegistry.getEmailThreadService().disconnectGmail(connectionId);
      setConnections((prev) => prev.filter((c) => c.id !== connectionId));
      toast.success(t('disconnected'));
    } catch {
      toast.error(t('disconnectFailed'));
    } finally {
      setDisconnectingId(null);
    }
  };

  return (
    <div className="flex items-center gap-2 shrink-0">
      {!loading && connections.length > 0 && (
        <Button variant="ghost" size="sm" onClick={() => setListOpen(true)}>
          <Mail className="mr-1.5 h-3.5 w-3.5" />
          {t('connectedCount', { count: String(connections.length) })}
        </Button>
      )}

      <Button variant="outline" size="sm" onClick={() => setConnectOpen(true)}>
        <Mail className="mr-2 h-4 w-4" />
        {connections.length > 0 ? t('connectAnother') : t('connectGmail')}
      </Button>

      <ConnectGmailDialog
        open={connectOpen}
        onOpenChange={(open) => {
          setConnectOpen(open);
          if (!open) load();
        }}
        companyId={companyId}
        companyName={companyName}
      />

      <Dialog open={listOpen} onOpenChange={setListOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('title')}</DialogTitle>
          </DialogHeader>
          <div className="max-h-96 space-y-2 overflow-y-auto">
            {connections.map((connection) => (
              <div
                key={connection.id}
                className="flex items-start justify-between gap-3 rounded-md border px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="truncate text-sm font-medium" title={connection.email_address}>
                      {connection.email_address}
                    </span>
                    <Badge variant={syncStatusTone[connection.sync_status] ?? 'secondary'} className="shrink-0">
                      {isSyncStatus(connection.sync_status) ? t(`syncStatus.${connection.sync_status}`) : connection.sync_status}
                    </Badge>
                  </div>
                  {connection.sync_status === 'error' && (
                    <p className="mt-1 text-xs text-destructive">
                      {/* sync_error_message is the provider's raw text (e.g. "invalid_grant"), kept for support, never shown (FIN-1272). */}
                      {t('reconnectNeeded')}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('connected', { time: relativeTime(connection.created_at) })}
                  </p>
                </div>
                <button
                  type="button"
                  aria-label={t('disconnect', { email: connection.email_address })}
                  disabled={disconnectingId === connection.id}
                  onClick={() => handleDisconnect(connection.id)}
                  className="shrink-0 rounded-full p-1 hover:bg-muted"
                >
                  {disconnectingId === connection.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <X className="h-3.5 w-3.5" />
                  )}
                </button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
